// @ts-strict-ignore
import * as React from "react";
import { CollapseContainer, CollapseHeader, CollapseBody } from "./styles";
import { HubbleBody } from "components/parts/Typography";
import { FramerLazyMotion } from "components/site/FramerLazyMotion";
import { IconMinusCircleOutlined, IconPlusCircleOutlined } from "@hubble/icons";
import { useTheme } from "@emotion/react";
import { spacing } from "@hubble/tokens/web";
import { Flex } from "@hubble/web";

type Props = {
  initialOpenedIndex?: number;
  children: React.ReactNode[];
};

const Accordion = ({ initialOpenedIndex = -1, children }: Props) => {
  const [openedIndex, setOpenIndex] = React.useState(initialOpenedIndex);
  const toggleOpenIndex = (index) => {
    setOpenIndex((prevIndex) => (index === prevIndex ? -1 : index));
  };
  return (
    <React.Fragment>
      {React.Children.map(children, (child, index) => {
        if (React.isValidElement(child)) {
          return React.cloneElement(
            child as React.ReactElement<{
              isOpen: boolean;
              handleToggle: () => void;
              isLast: boolean;
            }>,
            {
              isOpen: openedIndex === index,
              handleToggle: () => toggleOpenIndex(index),
              isLast: index === children.length - 1
            }
          );
        }
      })}
    </React.Fragment>
  );
};

type CollapseProps = {
  title?: string | React.ReactNode;
  customTitle?: (isOpen: boolean) => JSX.Element;
  isOpen?: boolean;
  isLast?: boolean;
  boldTitle?: boolean;
  titleAs?: React.ElementType;
  handleToggle?: () => void;
  isMobileNav?: boolean;
  children: React.ReactNode;
};

const Collapse = ({
  title,
  customTitle, // allow the header to be customized
  isOpen = false,
  boldTitle = true,
  handleToggle = () => {},
  isLast = false,
  isMobileNav,
  children,
  titleAs = "h6",
  ...props
}: CollapseProps) => {
  const { colorScheme } = useTheme();
  return (
    <CollapseContainer aria-expanded={isOpen} {...props}>
      <FramerLazyMotion>
        <CollapseHeader onClick={handleToggle} data-testid={"collapse-header"}>
          {Boolean(customTitle) ? (
            customTitle(isOpen)
          ) : (
            <React.Fragment>
              {React.isValidElement(title) ? (
                title
              ) : (
                <HubbleBody semiBold={boldTitle} as={titleAs}>
                  {title}
                </HubbleBody>
              )}
              {title && (
                <Flex
                  alignItems="center"
                  justifyContent="center"
                  css={`
                    min-width: ${spacing[32]} 0;
                  `}
                  ml={3}
                >
                  {isOpen ? (
                    <IconMinusCircleOutlined
                      color={colorScheme.content.primary}
                    />
                  ) : (
                    <IconPlusCircleOutlined
                      color={colorScheme.content.primary}
                    />
                  )}
                </Flex>
              )}
            </React.Fragment>
          )}
        </CollapseHeader>
        <div
          css={`
            padding: ${spacing[12]} 0;
          `}
        >
          <CollapseBody
            transition={{ duration: 0.2 }}
            animate={{ height: isOpen ? "auto" : 0 }}
          >
            {children}
          </CollapseBody>
        </div>
      </FramerLazyMotion>
    </CollapseContainer>
  );
};

Accordion.Collapse = Collapse;

export default Accordion;
